import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { OUTREACH_FATIGUE_CAP_PER_WEEK } from 'fym-shared/src/music24/outreach/networkRules';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { FitRing } from '../components/FitRing';
import { ContactPoints } from '../components/ContactPoints';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { ReleaseOption } from '../hooks/useReleaseOptions';
import type { TargetDetail } from '../outreachTypes';
import { outreachBuilderPath } from '../outreachPaths';
import { TARGET_KIND_LABEL } from '../outreachLabels';
import { compactCount, shortDate } from '../outreachFormat';
import { outreachMessages } from '../outreachMessages';
import { TargetRules } from './TargetRules';
import { EditorialHelper } from './EditorialHelper';
import { findMessages as messages } from './findMessages';
import { TargetCover } from './TargetCover';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Find.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TargetDrawerBodyProps {
  accountId: number;
  detail: TargetDetail;
  song: ReleaseOption | null;
  onClose: () => void;
}

/**
 * The facts of one target (design outreach c2): size, the artists like you on it, the contact
 * points with their source (an email shows after a reveal), the rules, the history and the pitch
 * button. An editorial playlist gets the Spotify for Artists helper instead of the contact (m3).
 */
const TargetDrawerBodyComponent: React.FC<TargetDrawerBodyProps> = ({ accountId, detail, song, onClose }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const plan = useOutreachPlan();
  const [s4aOpen, setS4aOpen] = useState(false);
  const isEditorial = detail.flags.includes('editorial');
  const kind = intl.formatMessage(TARGET_KIND_LABEL[detail.kind]);
  const similar = useMemo(
    () =>
      detail.similarArtistNames.map((name) => (
        <div key={name} className={shared.miniRow}>
          <b>{name}</b>
        </div>
      )),
    [detail.similarArtistNames]
  );
  const history = useMemo(
    () =>
      detail.history.map((row) => (
        <span key={row.campaignUuid} className={shared.small}>
          {intl.formatMessage(messages.historyRow, { campaign: row.campaignName, date: shortDate(intl, row.sentAt) })}
        </span>
      )),
    [detail.history, intl]
  );

  const pitch = useCallback(() => {
    plan.guard(SMLUpgradeFeature.outreach, () => {
      // Outreach v2: Plug picks the people every day, so the setup takes the song only.
      // eslint-disable-next-line no-void
      void navigate(outreachBuilderPath({ song: song?.catalogItemUuid ?? null }));
    });
  }, [navigate, plan, song]);

  return (
    <>
      <div className={shared.drawerHead}>
        <TargetCover className={styles.drawerCover} imageUrl={detail.imageUrl} kind={detail.kind} />
        <div className={shared.grow}>
          <span className={shared.eyebrow}>
            {detail.contactName === null
              ? kind
              : intl.formatMessage(messages.drawerBy, { kind, name: detail.contactName })}
          </span>
          <h3 className={`${shared.h2} mt-1`}>{detail.name}</h3>
          <a className={shared.small} href={detail.url} target="_blank" rel="noopener noreferrer">
            {intl.formatMessage(outreachMessages.openLink)}
          </a>
        </div>
        <button
          type="button"
          className="btn btn-link text-reset"
          aria-label={intl.formatMessage(outreachMessages.close)}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      <div className={shared.kv}>
        <div className={shared.kvCell}>
          <div className={shared.kvKey}>{intl.formatMessage(messages.drawerFollowers)}</div>
          <div className={shared.kvValue}>{compactCount(intl, detail.followers)}</div>
        </div>
        <div className={shared.kvCell}>
          <div className={shared.kvKey}>{intl.formatMessage(messages.drawerCountry)}</div>
          <div className={shared.kvValue}>{detail.country ?? '—'}</div>
        </div>
        <div className={shared.kvCell}>
          <div className={shared.kvKey}>{intl.formatMessage(messages.drawerFit)}</div>
          <FitRing score={detail.fit.score} />
        </div>
      </div>
      {similar.length > 0 && (
        <div>
          <span className={`${shared.eyebrow} mb-2`}>{intl.formatMessage(messages.similarOnIt)}</span>
          <div className={shared.miniList}>{similar}</div>
        </div>
      )}
      {isEditorial ? (
        <EditorialHelper
          accountId={accountId}
          song={song}
          isOpen={s4aOpen}
          onOpen={() => setS4aOpen(true)}
          onClose={() => setS4aOpen(false)}
        />
      ) : (
        <div>
          <span className={`${shared.eyebrow} mb-2`}>{intl.formatMessage(messages.contactTitle)}</span>
          {detail.points.length === 0 ? (
            <p className={shared.small}>{intl.formatMessage(messages.noContact)}</p>
          ) : (
            <ContactPoints
              accountId={accountId}
              contactUuid={detail.contact?.contactUuid ?? null}
              points={detail.points}
            />
          )}
        </div>
      )}
      <TargetRules rules={detail.rules} />
      <div className="d-flex flex-column gap-1">
        <span className={shared.eyebrow}>{intl.formatMessage(messages.historyTitle)}</span>
        {history.length === 0 ? (
          <span className={shared.small}>{intl.formatMessage(messages.historyNone)}</span>
        ) : (
          history
        )}
        <span className={shared.small}>
          {intl.formatMessage(messages.networkRule, { cap: OUTREACH_FATIGUE_CAP_PER_WEEK })}
        </span>
      </div>
      {!isEditorial && (
        <div className={shared.row}>
          <SMLButton
            color="primary"
            omitCap
            leftIcon={faPaperPlane}
            onClick={pitch}
            disabled={plan.isDemo || detail.fatigue !== null}
          >
            {song === null
              ? intl.formatMessage(messages.pitchToNoSong, { name: detail.contactName ?? detail.name })
              : intl.formatMessage(messages.pitchTo, { song: song.title, name: detail.contactName ?? detail.name })}
          </SMLButton>
        </div>
      )}
    </>
  );
};

export const TargetDrawerBody = TypedMemo(TargetDrawerBodyComponent);
